import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../config/api';
import './dashboard.css';

function SupplierDashboard() {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showQuoteForm, setShowQuoteForm] = useState(false);
  const [showRequestDetails, setShowRequestDetails] = useState(false);
  const [showAvailableQuotes, setShowAvailableQuotes] = useState(false);
  const [availableQuotes, setAvailableQuotes] = useState([]);
  const [activeMenu, setActiveMenu] = useState('requests');
  const [myQuotes, setMyQuotes] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [showMaterialForm, setShowMaterialForm] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState(null);
  const [additionalServices, setAdditionalServices] = useState({
    grounding_lightning_rod_price: '',
    fire_fighting_system_price: '',
    installation_delivery_maintenance_price: '',
  });
  const [servicesLoading, setServicesLoading] = useState(false);
  const [materialFormData, setMaterialFormData] = useState({
    material_type: 'panel',
    panel_size_length: '',
    panel_size_width: '',
    panel_size_sqm: '',
    panel_output_watts: '',
    panel_price: '',
    mounting_frame_price: '',
    electrical_price: '',
    available_quantity: '',
    battery_capacity_kwh: '',
    battery_dod_percentage: '',
    inverter_capacity_kw: '',
    brand: '',
    description: '',
    is_active: true,
  });
  const [user, setUser] = useState(null);
  const [profileData, setProfileData] = useState({
    name: '',
    geographic_range: '',
    main_office_address: '',
  });
  const [profileLoading, setProfileLoading] = useState(false);
  const [quoteData, setQuoteData] = useState({
    customer_request_id: null,
    quote_details: [],
    total_price: '',
    notes: '',
  });

  useEffect(() => {
    // Check authentication on component mount
    const token = localStorage.getItem('supplier_token');
    const userData = localStorage.getItem('supplier_user');
    
    if (!token || !userData) {
      navigate('/supplier-login');
      return;
    }
    
    if (userData) {
      const parsedUser = JSON.parse(userData);
      setUser(parsedUser);
      // Initialize profile data
      setProfileData({
        name: parsedUser.name || '',
        geographic_range: parsedUser.geographic_range || '',
        main_office_address: parsedUser.main_office_address || '',
      });
    }
    
    // Fetch updated user data
    fetchUser();
  }, [navigate]);

  const fetchUser = async () => {
    try {
      const token = localStorage.getItem('supplier_token');
      
      if (!token) {
        return;
      }
      
      const headers = {
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`,
      };
      
      const response = await fetch(`${API_BASE_URL}/supplier/user`, {
        headers: headers,
      });

      const data = await response.json();

      if (data.success && data.data) {
        setUser(data.data);
        // Update localStorage
        localStorage.setItem('supplier_user', JSON.stringify(data.data));
        // Update profile data
        setProfileData({
          name: data.data.name || '',
          geographic_range: data.data.geographic_range || '',
          main_office_address: data.data.main_office_address || '',
        });
      }
    } catch (err) {
      console.error('Error fetching user:', err);
    }
  };

  useEffect(() => {
    if (activeMenu === 'requests') {
      fetchRequests();
    } else if (activeMenu === 'quotes') {
      fetchMyQuotes();
    } else if (activeMenu === 'profile') {
      fetchUser();
    } else if (activeMenu === 'materials') {
      fetchMaterials();
    } else if (activeMenu === 'additional-services') {
      fetchAdditionalServices();
    }
  }, [activeMenu]);

  const handleLogout = async () => {
    try {
      const token = localStorage.getItem('supplier_token');
      
      if (token) {
        await fetch(`${API_BASE_URL}/supplier/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json',
          },
        });
      }
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      localStorage.removeItem('supplier_token');
      localStorage.removeItem('supplier_user');
      navigate('/supplier-login');
    }
  };

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('supplier_token');
      
      if (!token) {
        navigate('/supplier-login');
        return;
      }
      
      const headers = {
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`,
      };
      
      const response = await fetch(`${API_BASE_URL}/supplier/requests`, {
        headers: headers,
      });

      const data = await response.json();

      console.log('API Response:', data);
      console.log('Requests data:', data.data);
      console.log('Requests count:', data.data?.length || 0);

      if (data.success) {
        console.log('Setting requests:', data.data);
        setRequests(data.data || []);
      } else {
        console.error('API Error:', data.message);
        setError(data.message || 'Failed to fetch requests');
      }
    } catch (err) {
      console.error('Error fetching requests:', err);
      setError('Failed to load customer requests');
    } finally {
      setLoading(false);
    }
  };

  const fetchMyQuotes = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('supplier_token');
      
      if (!token) {
        navigate('/supplier-login');
        return;
      }
      
      const headers = {
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`,
      };
      
      const response = await fetch(`${API_BASE_URL}/supplier/quotes`, {
        headers: headers,
      });

      const data = await response.json();

      if (data.success) {
        setMyQuotes(data.data);
      } else {
        setError(data.message || 'Failed to fetch quotes');
      }
    } catch (err) {
      console.error('Error fetching quotes:', err);
      setError('Failed to load quotes');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (request) => {
    console.log('Viewing details for request:', request);
    console.log('Setting selectedRequest and showRequestDetails to true');
    // Ensure we have all the request data
    setSelectedRequest(request);
    setShowRequestDetails(true);
    console.log('State updated - showRequestDetails should be true now');
  };

  const calculateAvailableQuotes = async (request) => {
    console.log('Calculating available quotes for request:', request);
    try {
      setLoading(true);
      
      // Fetch additional services
      const token = localStorage.getItem('supplier_token');
      const headers = {
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`,
      };
      
      // Get additional services
      const servicesResponse = await fetch(`${API_BASE_URL}/supplier/additional-services`, {
        headers: headers,
      });
      const servicesData = await servicesResponse.json();
      const services = servicesData.success ? servicesData.data : {};
      console.log('Additional services:', services);
      
      // Get inverters
      const invertersResponse = await fetch(`${API_BASE_URL}/supplier/materials`, {
        headers: headers,
      });
      const invertersData = await invertersResponse.json();
      const inverters = invertersData.success 
        ? invertersData.data.filter(m => m.material_type === 'inverter' && m.is_active)
        : [];
      console.log('Inverters:', inverters);
      
      // Calculate all possible combinations
      const quotes = [];
      const panels = request.supplier_panels_calculation || [];
      const batteries = request.supplier_batteries_calculation || [];
      console.log('Panels:', panels);
      console.log('Batteries:', batteries);
      
      // For each panel + battery + inverter combination
      panels.forEach(panel => {
        batteries.forEach(battery => {
          inverters.forEach(inverter => {
            // Calculate total price
            // (سعر الهيكل المعدني + الكهربائيات + سعر اللوح) × عدد الألواح
            const panelPricePerUnit = (parseFloat(panel.panel_price || 0) + 
                                      parseFloat(panel.mounting_frame_price || 0) + 
                                      parseFloat(panel.electrical_price || 0));
            const panelsTotalPrice = panelPricePerUnit * (panel.calculated_panels_count || 0);
            
            // سعر البطارية × عدد البطاريات
            const batteriesTotalPrice = (parseFloat(battery.battery_price || 0)) * (battery.calculated_batteries_count || 0);
            
            // سعر الانفرتر
            const inverterPrice = parseFloat(inverter.panel_price || 0);
            
            // التكاليف الإضافية
            const groundingPrice = parseFloat(services.grounding_lightning_rod_price || 0);
            const fireFightingPrice = parseFloat(services.fire_fighting_system_price || 0);
            const installationPrice = parseFloat(services.installation_delivery_maintenance_price || 0);
            
            // السعر الإجمالي
            const totalPrice = panelsTotalPrice + 
                             batteriesTotalPrice + 
                             inverterPrice + 
                             groundingPrice + 
                             fireFightingPrice + 
                             installationPrice;
            
            quotes.push({
              panel: panel,
              battery: battery,
              inverter: inverter,
              panelsTotalPrice: panelsTotalPrice,
              batteriesTotalPrice: batteriesTotalPrice,
              inverterPrice: inverterPrice,
              groundingPrice: groundingPrice,
              fireFightingPrice: fireFightingPrice,
              installationPrice: installationPrice,
              totalPrice: totalPrice,
            });
          });
        });
      });
      
      // Sort by total price
      quotes.sort((a, b) => a.totalPrice - b.totalPrice);
      
      console.log('Calculated quotes:', quotes);
      console.log('Setting availableQuotes and showAvailableQuotes to true');
      setAvailableQuotes(quotes);
      setSelectedRequest(request);
      setShowAvailableQuotes(true);
      console.log('State updated - showAvailableQuotes should be true now');
    } catch (err) {
      console.error('Error calculating quotes:', err);
      alert('حدث خطأ أثناء حساب العروض: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddQuoteMaterial = () => {
    setQuoteData(prev => ({
      ...prev,
      quote_details: [
        ...prev.quote_details,
        { material_name: '', quantity: '', unit_price: '' }
      ]
    }));
  };

  const handleMaterialChange = (index, field, value) => {
    setQuoteData(prev => {
      const newDetails = [...prev.quote_details];
      newDetails[index] = { ...newDetails[index], [field]: value };
      
      if (field === 'quantity' || field === 'unit_price') {
        const qty = parseFloat(newDetails[index].quantity) || 0;
        const price = parseFloat(newDetails[index].unit_price) || 0;
        newDetails[index].total_price = (qty * price).toFixed(2);
      }
      
      const total = newDetails.reduce((sum, item) => {
        return sum + (parseFloat(item.total_price) || 0);
      }, 0);
      
      return {
        ...prev,
        quote_details: newDetails,
        total_price: total.toFixed(2)
      };
    });
  };

  const handleRemoveMaterial = (index) => {
    setQuoteData(prev => ({
      ...prev,
      quote_details: prev.quote_details.filter((_, i) => i !== index)
    }));
  };

  const handleProfileChange = (field, value) => {
    setProfileData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const fetchAdditionalServices = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('supplier_token');
      
      if (!token) {
        navigate('/supplier-login');
        return;
      }
      
      const headers = {
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`,
      };
      
      const response = await fetch(`${API_BASE_URL}/supplier/additional-services`, {
        headers: headers,
      });

      const data = await response.json();

      if (data.success && data.data) {
        setAdditionalServices({
          grounding_lightning_rod_price: data.data.grounding_lightning_rod_price || '',
          fire_fighting_system_price: data.data.fire_fighting_system_price || '',
          installation_delivery_maintenance_price: data.data.installation_delivery_maintenance_price || '',
        });
      } else {
        setError(data.message || 'Failed to fetch additional services');
      }
    } catch (err) {
      console.error('Error fetching additional services:', err);
      setError('Failed to load additional services');
    } finally {
      setLoading(false);
    }
  };

  const handleAdditionalServicesChange = (field, value) => {
    setAdditionalServices(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAdditionalServicesSubmit = async (e) => {
    e.preventDefault();
    setServicesLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('supplier_token');
      
      if (!token) {
        navigate('/supplier-login');
        return;
      }
      
      const headers = {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`,
      };
      
      const response = await fetch(`${API_BASE_URL}/supplier/additional-services`, {
        method: 'PUT',
        headers: headers,
        body: JSON.stringify(additionalServices),
      });

      const data = await response.json();

      if (data.success) {
        alert('تم حفظ الأسعار بنجاح');
        if (data.data) {
          setAdditionalServices({
            grounding_lightning_rod_price: data.data.grounding_lightning_rod_price || '',
            fire_fighting_system_price: data.data.fire_fighting_system_price || '',
            installation_delivery_maintenance_price: data.data.installation_delivery_maintenance_price || '',
          });
        }
      } else {
        setError(data.message || 'فشل حفظ الأسعار');
        alert(data.message || 'فشل حفظ الأسعار');
      }
    } catch (err) {
      console.error('Error saving additional services:', err);
      setError('حدث خطأ أثناء حفظ الأسعار');
      alert('حدث خطأ أثناء حفظ الأسعار');
    } finally {
      setServicesLoading(false);
    }
  };

  const fetchMaterials = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('supplier_token');
      
      if (!token) {
        navigate('/supplier-login');
        return;
      }
      
      const headers = {
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`,
      };
      
      const response = await fetch(`${API_BASE_URL}/supplier/materials`, {
        headers: headers,
      });

      const data = await response.json();

      if (data.success) {
        setMaterials(data.data);
      } else {
        setError(data.message || 'Failed to fetch materials');
      }
    } catch (err) {
      console.error('Error fetching materials:', err);
      setError('Failed to load materials');
    } finally {
      setLoading(false);
    }
  };

  const handleAddMaterial = () => {
    setEditingMaterial(null);
    setMaterialFormData({
      material_type: 'panel',
      panel_size_length: '',
      panel_size_width: '',
      panel_output_watts: '',
      panel_price: '',
      mounting_frame_price: '',
      electrical_price: '',
      brand: '',
      description: '',
      is_active: true,
    });
    setShowMaterialForm(true);
  };

  const handleEditMaterial = (material) => {
    setEditingMaterial(material);
    setMaterialFormData({
      material_type: material.material_type || 'panel',
      panel_size_length: material.panel_size_length || '',
      panel_size_width: material.panel_size_width || '',
      panel_size_sqm: material.panel_size_sqm || '',
      panel_output_watts: material.panel_output_watts || '',
      panel_price: material.panel_price || '',
      mounting_frame_price: material.mounting_frame_price || '',
      electrical_price: material.electrical_price || '',
      available_quantity: material.available_quantity || '',
      battery_capacity_kwh: material.battery_capacity_kwh || '',
      battery_dod_percentage: material.battery_dod_percentage || '',
      inverter_capacity_kw: material.inverter_capacity_kw || '',
      brand: material.brand || '',
      description: material.description || '',
      is_active: material.is_active !== undefined ? material.is_active : true,
    });
    setShowMaterialForm(true);
  };

  const handleMaterialFormChange = (field, value) => {
    setMaterialFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleMaterialSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('supplier_token');
      
      if (!token) {
        navigate('/supplier-login');
        return;
      }
      
      const headers = {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`,
      };
      
      const url = editingMaterial 
        ? `${API_BASE_URL}/supplier/materials/${editingMaterial.id}`
        : `${API_BASE_URL}/supplier/materials`;
      
      const method = editingMaterial ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method: method,
        headers: headers,
        body: JSON.stringify(materialFormData),
      });

      const data = await response.json();

      if (data.success) {
        alert(editingMaterial ? 'تم تحديث المادة بنجاح' : 'تم إضافة المادة بنجاح');
        setShowMaterialForm(false);
        fetchMaterials();
      } else {
        setError(data.message || 'فشل حفظ المادة');
        alert(data.message || 'فشل حفظ المادة');
      }
    } catch (err) {
      console.error('Error saving material:', err);
      setError('حدث خطأ أثناء حفظ المادة');
      alert('حدث خطأ أثناء حفظ المادة');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteMaterial = async (id) => {
    if (!window.confirm('هل أنت متأكد من حذف هذه المادة؟')) {
      return;
    }

    try {
      const token = localStorage.getItem('supplier_token');
      
      if (!token) {
        navigate('/supplier-login');
        return;
      }
      
      const headers = {
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`,
      };
      
      const response = await fetch(`${API_BASE_URL}/supplier/materials/${id}`, {
        method: 'DELETE',
        headers: headers,
      });

      const data = await response.json();

      if (data.success) {
        alert('تم حذف المادة بنجاح');
        fetchMaterials();
      } else {
        alert(data.message || 'فشل حذف المادة');
      }
    } catch (err) {
      console.error('Error deleting material:', err);
      alert('حدث خطأ أثناء حذف المادة');
    }
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setProfileLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('supplier_token');
      
      if (!token) {
        navigate('/supplier-login');
        return;
      }
      
      const headers = {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`,
      };
      
      const response = await fetch(`${API_BASE_URL}/supplier/profile`, {
        method: 'PUT',
        headers: headers,
        body: JSON.stringify(profileData),
      });

      const data = await response.json();

      if (data.success) {
        alert('تم تحديث المعلومات بنجاح');
        // Update user state
        if (data.data) {
          setUser(data.data);
          localStorage.setItem('supplier_user', JSON.stringify(data.data));
        }
      } else {
        setError(data.message || 'فشل تحديث المعلومات');
        alert(data.message || 'فشل تحديث المعلومات');
      }
    } catch (err) {
      console.error('Error updating profile:', err);
      setError('حدث خطأ أثناء تحديث المعلومات');
      alert('حدث خطأ أثناء تحديث المعلومات');
    } finally {
      setProfileLoading(false);
    }
  };

  const handleQuoteSubmit = async (e) => {
    e.preventDefault();
    
    if (quoteData.quote_details.length === 0) {
      alert('يرجى إضافة مادة واحدة على الأقل');
      return;
    }
    
    try {
      const token = localStorage.getItem('supplier_token');
      
      if (!token) {
        navigate('/supplier-login');
        return;
      }
      
      const headers = {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`,
      };
      
      const response = await fetch(`${API_BASE_URL}/supplier/quotes`, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(quoteData),
      });

      const data = await response.json();

      if (data.success) {
        alert('تم إنشاء العرض بنجاح');
        setShowQuoteForm(false);
        setQuoteData({
          customer_request_id: null,
          quote_details: [],
          total_price: '',
          notes: '',
        });
        if (activeMenu === 'quotes') {
          fetchMyQuotes();
        }
      } else {
        alert(data.message || 'فشل إنشاء العرض');
      }
    } catch (err) {
      console.error('Error creating quote:', err);
      alert('حدث خطأ أثناء إنشاء العرض');
    }
  };

  return (
    <div className="supplier-dashboard-wrapper">
      {/* Top Bar */}
      <div className="dashboard-topbar">
        <div className="topbar-content">
          <div className="topbar-left">
            <h2 className="dashboard-logo">
              <i className="fas fa-solar-panel"></i>
              لوحة تحكم المورد
            </h2>
          </div>
          <div className="topbar-right">
            <div className="topbar-user">
              <i className="fas fa-user-circle"></i>
              <span>{user?.name || 'المورد'}</span>
            </div>
            <button onClick={handleLogout} className="topbar-logout" title="تسجيل الخروج">
              <i className="fas fa-sign-out-alt"></i>
            </button>
          </div>
        </div>
      </div>

      <div className="dashboard-container">
        {/* Sidebar */}
        <div className="dashboard-sidebar">
          <ul className="sidebar-menu">
            <li className={activeMenu === 'requests' ? 'active' : ''}>
              <a href="#" onClick={(e) => { e.preventDefault(); setActiveMenu('requests'); }}>
                <i className="fas fa-file-alt"></i>
                <span>العروض</span>
              </a>
            </li>
            <li className={activeMenu === 'quotes' ? 'active' : ''}>
              <a href="#" onClick={(e) => { e.preventDefault(); setActiveMenu('quotes'); }}>
                <i className="fas fa-quote-right"></i>
                <span>عروضي</span>
              </a>
            </li>
            <li className={activeMenu === 'profile' ? 'active' : ''}>
              <a href="#" onClick={(e) => { e.preventDefault(); setActiveMenu('profile'); }}>
                <i className="fas fa-user-circle"></i>
                <span>معلوماتي</span>
              </a>
            </li>
            <li className={activeMenu === 'materials' ? 'active' : ''}>
              <a href="#" onClick={(e) => { e.preventDefault(); setActiveMenu('materials'); }}>
                <i className="fas fa-boxes"></i>
                <span>موادي</span>
              </a>
            </li>
            <li className={activeMenu === 'additional-services' ? 'active' : ''}>
              <a href="#" onClick={(e) => { e.preventDefault(); setActiveMenu('additional-services'); }}>
                <i className="fas fa-dollar-sign"></i>
                <span>التكاليف الإضافية</span>
              </a>
            </li>
          </ul>
        </div>

        {/* Main Content */}
        <div className="dashboard-main">
          {error && (
            <div className="alert alert-error">
              <i className="fas fa-exclamation-circle"></i>
              {error}
            </div>
          )}

          {loading && (
            <div className="loading-spinner">
              <i className="fas fa-spinner fa-spin"></i>
              <p>جاري التحميل...</p>
            </div>
          )}

          {!loading && activeMenu === 'requests' && (
            <div>
              <h2>طلبات العملاء</h2>
              {requests.length === 0 ? (
                <p>لا توجد طلبات حالياً</p>
              ) : (
                <div className="requests-grid">
                  {requests.map(request => (
                    <div key={request.id} className="request-card">
                      <h3>{request.full_name}</h3>
                      <p><strong>الهاتف:</strong> {request.phone_number}</p>
                      <p><strong>المساحة:</strong> {request.area_sqm} م²</p>
                      
                      {/* Basic Consumption Info */}
                      {request.daytime_consumption_amps && (
                        <p style={{ marginTop: '10px', color: '#4a5568' }}>
                          <strong>الاستهلاك النهاري:</strong> {request.daytime_consumption_amps} أمبير
                        </p>
                      )}
                      
                      {request.nightly_consumption_amps_per_hour && request.battery_hours_night && (
                        <p style={{ marginTop: '5px', fontSize: '14px', color: '#718096' }}>
                          <strong>الاستهلاك الليلي:</strong> {request.nightly_consumption_amps_per_hour} أمبير/ساعة × {request.battery_hours_night} ساعة
                        </p>
                      )}
                      
                      <div className="card-actions">
                        <button 
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleViewDetails(request);
                          }}
                          style={{ flex: 1 }}
                        >
                          <i className="fas fa-eye"></i> عرض التفاصيل
                        </button>
                        <button 
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            calculateAvailableQuotes(request);
                          }} 
                          className="btn-primary"
                          style={{ flex: 1 }}
                        >
                          <i className="fas fa-list"></i> العروض المتوفرة
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {!loading && activeMenu === 'quotes' && (
            <div>
              <h2>عروض الأسعار الخاصة بي</h2>
              {myQuotes.length === 0 ? (
                <div style={{ background: 'white', padding: '40px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', textAlign: 'center', color: '#718096' }}>
                  <i className="fas fa-file-invoice-dollar" style={{ fontSize: '48px', marginBottom: '15px' }}></i>
                  <p style={{ fontSize: '18px', marginBottom: '10px' }}>لا توجد عروض حالياً</p>
                  <p style={{ fontSize: '14px' }}>لم تقم بإنشاء أي عروض أسعار بعد. يمكنك إنشاء عروض من قسم "طلبات العملاء"</p>
                </div>
              ) : (
                <div className="quotes-grid">
                  {myQuotes.map(quote => (
                    <div key={quote.id} className="quote-card">
                      <h3>عرض #{quote.id}</h3>
                      <p><strong>السعر الإجمالي:</strong> {quote.total_price} ريال</p>
                      <p><strong>الحالة:</strong> {quote.status}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeMenu === 'profile' && (
            <div>
              <h2>معلومات المورد</h2>
              <div className="profile-form-container">
                <form onSubmit={handleProfileSubmit} className="profile-form">
                  <div className="form-group">
                    <label>
                      <i className="fas fa-user"></i>
                      الاسم الكامل
                    </label>
                    <input
                      type="text"
                      value={profileData.name}
                      onChange={(e) => handleProfileChange('name', e.target.value)}
                      placeholder="أدخل اسمك الكامل"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>
                      <i className="fas fa-map-marked-alt"></i>
                      النطاق الجغرافي
                    </label>
                    <select
                      value={profileData.geographic_range}
                      onChange={(e) => handleProfileChange('geographic_range', e.target.value)}
                    >
                      <option value="">اختر المحافظة</option>
                      <option value="بغداد">بغداد</option>
                      <option value="البصرة">البصرة</option>
                      <option value="نينوى">نينوى</option>
                      <option value="أربيل">أربيل</option>
                      <option value="السليمانية">السليمانية</option>
                      <option value="النجف">النجف</option>
                      <option value="كربلاء">كربلاء</option>
                      <option value="بابل">بابل</option>
                      <option value="واسط">واسط</option>
                      <option value="ميسان">ميسان</option>
                      <option value="المثنى">المثنى</option>
                      <option value="القادسية">القادسية</option>
                      <option value="ذي قار">ذي قار</option>
                      <option value="ديالى">ديالى</option>
                      <option value="صلاح الدين">صلاح الدين</option>
                      <option value="كركوك">كركوك</option>
                      <option value="الأنبار">الأنبار</option>
                      <option value="دهوك">دهوك</option>
                    </select>
                    <small className="form-hint">حدد المحافظة التي تقدم فيها خدماتك</small>
                  </div>

                  <div className="form-group">
                    <label>
                      <i className="fas fa-building"></i>
                      عنوان المكتب الرئيسي
                    </label>
                    <textarea
                      value={profileData.main_office_address}
                      onChange={(e) => handleProfileChange('main_office_address', e.target.value)}
                      placeholder="أدخل عنوان المكتب الرئيسي بالتفصيل"
                      rows="4"
                    />
                    <small className="form-hint">أدخل العنوان الكامل للمكتب الرئيسي</small>
                  </div>

                  <div className="form-actions">
                    <button 
                      type="submit" 
                      className="btn-primary" 
                      disabled={profileLoading}
                    >
                      {profileLoading ? (
                        <>
                          <i className="fas fa-spinner fa-spin"></i>
                          جاري الحفظ...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-save"></i>
                          حفظ التغييرات
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {!loading && activeMenu === 'materials' && (
            <div>
              <h2 style={{ marginBottom: '30px' }}>موادي</h2>
              
              {/* Panels Section */}
              <div className="material-section">
                <div className="section-header">
                  <h3>
                    <i className="fas fa-solar-panel"></i>
                    الألواح الشمسية
                  </h3>
                  <button 
                    onClick={() => {
                      setMaterialFormData({
                        material_type: 'panel',
                        panel_size_length: '',
                        panel_size_width: '',
                        panel_size_sqm: '',
                        panel_output_watts: '',
                        panel_price: '',
                        mounting_frame_price: '',
                        electrical_price: '',
                        available_quantity: '',
                        brand: '',
                        description: '',
                        is_active: true,
                      });
                      setEditingMaterial(null);
                      setShowMaterialForm(true);
                    }} 
                    className="btn-primary btn-sm"
                  >
                    <i className="fas fa-plus"></i> إضافة لوح
                  </button>
                </div>
                {materials.filter(m => m.material_type === 'panel').length === 0 ? (
                  <div className="empty-section">
                    <i className="fas fa-solar-panel" style={{ fontSize: '40px', color: '#cbd5e0', marginBottom: '10px' }}></i>
                    <p>لا توجد ألواح حالياً</p>
                  </div>
                ) : (
                  <div className="materials-grid">
                    {materials.filter(m => m.material_type === 'panel').map(material => (
                      <div key={material.id} className="material-card">
                        <div className="material-header">
                          <h3>
                            {material.brand ? `${material.brand} - ` : ''}
                            {material.panel_output_watts ? `${material.panel_output_watts} واط` : 'لوح شمسي'}
                          </h3>
                          <span className={`material-status ${material.is_active ? 'active' : 'inactive'}`}>
                            {material.is_active ? 'نشط' : 'غير نشط'}
                          </span>
                        </div>
                        
                        <div className="material-details">
                          {material.panel_size_sqm && (
                            <p><strong>القياس:</strong> {material.panel_size_sqm} م²</p>
                          )}
                          {material.panel_output_watts && (
                            <p><strong>الإنتاجية:</strong> {material.panel_output_watts} واط</p>
                          )}
                          {material.panel_price && (
                            <p><strong>سعر اللوح:</strong> ${material.panel_price}</p>
                          )}
                          {material.available_quantity !== undefined && (
                            <p><strong>العدد المتوفر:</strong> {material.available_quantity} لوح</p>
                          )}
                          {material.mounting_frame_price && (
                            <p><strong>سعر الهيكل المعدني:</strong> ${material.mounting_frame_price}</p>
                          )}
                          {material.electrical_price && (
                            <p><strong>سعر الكهربائيات:</strong> ${material.electrical_price}</p>
                          )}
                          {material.panel_price && material.mounting_frame_price && material.electrical_price && (
                            <p style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #e2e8f0', fontWeight: 'bold', color: '#667eea' }}>
                              <strong>السعر الإجمالي للوح:</strong> ${
                                (parseFloat(material.panel_price || 0) + parseFloat(material.mounting_frame_price || 0) + parseFloat(material.electrical_price || 0)).toFixed(2)
                              }
                            </p>
                          )}
                          {material.description && (
                            <p style={{ marginTop: '10px', fontSize: '14px', color: '#718096' }}>{material.description}</p>
                          )}
                        </div>
                        
                        <div className="card-actions">
                          <button onClick={() => handleEditMaterial(material)} className="btn-edit">
                            <i className="fas fa-edit"></i> تعديل
                          </button>
                          <button onClick={() => handleDeleteMaterial(material.id)} className="btn-delete">
                            <i className="fas fa-trash"></i> حذف
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Batteries Section */}
              <div className="material-section">
                <div className="section-header">
                  <h3>
                    <i className="fas fa-battery-full"></i>
                    البطاريات
                  </h3>
                  <button 
                    onClick={() => {
                      setMaterialFormData({
                        material_type: 'battery',
                        panel_size_length: '',
                        panel_size_width: '',
                        panel_size_sqm: '',
                        panel_output_watts: '',
                        panel_price: '',
                        mounting_frame_price: '',
                        electrical_price: '',
                        available_quantity: '',
                        battery_capacity_kwh: '',
                        battery_dod_percentage: '',
                        brand: '',
                        description: '',
                        is_active: true,
                      });
                      setEditingMaterial(null);
                      setShowMaterialForm(true);
                    }} 
                    className="btn-primary btn-sm"
                  >
                    <i className="fas fa-plus"></i> إضافة بطارية
                  </button>
                </div>
                {materials.filter(m => m.material_type === 'battery').length === 0 ? (
                  <div className="empty-section">
                    <i className="fas fa-battery-full" style={{ fontSize: '40px', color: '#cbd5e0', marginBottom: '10px' }}></i>
                    <p>لا توجد بطاريات حالياً</p>
                  </div>
                ) : (
                  <div className="materials-grid">
                    {materials.filter(m => m.material_type === 'battery').map(material => (
                      <div key={material.id} className="material-card">
                        <div className="material-header">
                          <h3>
                            {material.brand ? `${material.brand}` : 'بطارية'}
                            {material.battery_capacity_kwh ? ` - ${material.battery_capacity_kwh} كيلو واط/ساعة` : ''}
                          </h3>
                          <span className={`material-status ${material.is_active ? 'active' : 'inactive'}`}>
                            {material.is_active ? 'نشط' : 'غير نشط'}
                          </span>
                        </div>
                        
                        <div className="material-details">
                          {material.battery_capacity_kwh && (
                            <p><strong>حجم البطارية:</strong> {material.battery_capacity_kwh} كيلو واط/ساعة</p>
                          )}
                          {material.battery_dod_percentage && (
                            <p><strong>عمق التفريغ:</strong> {(parseFloat(material.battery_dod_percentage) * 100).toFixed(1)}%</p>
                          )}
                          {material.panel_price && (
                            <p><strong>السعر:</strong> ${material.panel_price}</p>
                          )}
                          {material.available_quantity !== undefined && (
                            <p><strong>العدد المتوفر:</strong> {material.available_quantity} بطارية</p>
                          )}
                          {material.description && (
                            <p style={{ marginTop: '10px', fontSize: '14px', color: '#718096' }}>{material.description}</p>
                          )}
                        </div>
                        
                        <div className="card-actions">
                          <button onClick={() => handleEditMaterial(material)} className="btn-edit">
                            <i className="fas fa-edit"></i> تعديل
                          </button>
                          <button onClick={() => handleDeleteMaterial(material.id)} className="btn-delete">
                            <i className="fas fa-trash"></i> حذف
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Inverters Section */}
              <div className="material-section">
                <div className="section-header">
                  <h3>
                    <i className="fas fa-exchange-alt"></i>
                    المحولات (Inverters)
                  </h3>
                  <button 
                    onClick={() => {
                      setMaterialFormData({
                        material_type: 'inverter',
                        panel_size_length: '',
                        panel_size_width: '',
                        panel_size_sqm: '',
                        panel_output_watts: '',
                        panel_price: '',
                        mounting_frame_price: '',
                        electrical_price: '',
                        available_quantity: '',
                        battery_capacity_kwh: '',
                        battery_dod_percentage: '',
                        inverter_capacity_kw: '',
                        brand: '',
                        description: '',
                        is_active: true,
                      });
                      setEditingMaterial(null);
                      setShowMaterialForm(true);
                    }} 
                    className="btn-primary btn-sm"
                  >
                    <i className="fas fa-plus"></i> إضافة محول
                  </button>
                </div>
                {materials.filter(m => m.material_type === 'inverter').length === 0 ? (
                  <div className="empty-section">
                    <i className="fas fa-exchange-alt" style={{ fontSize: '40px', color: '#cbd5e0', marginBottom: '10px' }}></i>
                    <p>لا توجد محولات حالياً</p>
                  </div>
                ) : (
                  <div className="materials-grid">
                    {materials.filter(m => m.material_type === 'inverter').map(material => (
                      <div key={material.id} className="material-card">
                        <div className="material-header">
                          <h3>
                            {material.brand ? `${material.brand}` : 'محول'}
                            {material.inverter_capacity_kw ? ` - ${material.inverter_capacity_kw} كيلو واط` : ''}
                          </h3>
                          <span className={`material-status ${material.is_active ? 'active' : 'inactive'}`}>
                            {material.is_active ? 'نشط' : 'غير نشط'}
                          </span>
                        </div>
                        
                        <div className="material-details">
                          {material.inverter_capacity_kw && (
                            <p><strong>حجم المحول:</strong> {material.inverter_capacity_kw} كيلو واط</p>
                          )}
                          {material.panel_price && (
                            <p><strong>السعر:</strong> ${material.panel_price}</p>
                          )}
                          {material.available_quantity !== undefined && (
                            <p><strong>العدد المتوفر:</strong> {material.available_quantity} محول</p>
                          )}
                          {material.description && (
                            <p style={{ marginTop: '10px', fontSize: '14px', color: '#718096' }}>{material.description}</p>
                          )}
                        </div>
                        
                        <div className="card-actions">
                          <button onClick={() => handleEditMaterial(material)} className="btn-edit">
                            <i className="fas fa-edit"></i> تعديل
                          </button>
                          <button onClick={() => handleDeleteMaterial(material.id)} className="btn-delete">
                            <i className="fas fa-trash"></i> حذف
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {!loading && activeMenu === 'additional-services' && (
            <div>
              <h2 style={{ marginBottom: '30px' }}>التكاليف الإضافية</h2>
              <div className="profile-form-container">
                <form onSubmit={handleAdditionalServicesSubmit} className="profile-form">
                  <div className="form-group">
                    <label>
                      <i className="fas fa-bolt"></i>
                      سعر التأريض ومانعة الصواعق ($)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={additionalServices.grounding_lightning_rod_price}
                      onChange={(e) => handleAdditionalServicesChange('grounding_lightning_rod_price', e.target.value)}
                      placeholder="مثال: 500"
                    />
                  </div>

                  <div className="form-group">
                    <label>
                      <i className="fas fa-fire-extinguisher"></i>
                      سعر منظومة مكافحة الحريق ($)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={additionalServices.fire_fighting_system_price}
                      onChange={(e) => handleAdditionalServicesChange('fire_fighting_system_price', e.target.value)}
                      placeholder="مثال: 800"
                    />
                  </div>

                  <div className="form-group">
                    <label>
                      <i className="fas fa-tools"></i>
                      سعر التركيب والتوصيل والنقل والصيانه والضمان ($)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={additionalServices.installation_delivery_maintenance_price}
                      onChange={(e) => handleAdditionalServicesChange('installation_delivery_maintenance_price', e.target.value)}
                      placeholder="مثال: 2000"
                    />
                  </div>

                  <div className="form-actions">
                    <button 
                      type="submit" 
                      className="btn-primary" 
                      disabled={servicesLoading}
                    >
                      {servicesLoading ? (
                        <>
                          <i className="fas fa-spinner fa-spin"></i>
                          جاري الحفظ...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-save"></i>
                          حفظ الأسعار
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {showMaterialForm && createPortal(
            <div 
              className="supplier-material-modal" 
              style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }} 
              onClick={(e) => { if (e.target.classList.contains('supplier-material-modal')) setShowMaterialForm(false); }}
            >
              <div className="supplier-material-modal-content" style={{ maxWidth: '700px', background: 'white', borderRadius: '16px', padding: '30px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }} onClick={(e) => e.stopPropagation()}>
                <h2>
                  {editingMaterial 
                    ? (editingMaterial.material_type === 'panel' ? 'تعديل لوح' : `تعديل ${editingMaterial.material_type === 'battery' ? 'بطارية' : editingMaterial.material_type === 'inverter' ? 'محول' : 'مادة'}`)
                    : (materialFormData.material_type === 'panel' ? 'إضافة لوح' : materialFormData.material_type === 'battery' ? 'إضافة بطارية' : materialFormData.material_type === 'inverter' ? 'إضافة محول' : 'إضافة مادة جديدة')
                  }
                </h2>
                <form onSubmit={handleMaterialSubmit}>
                  <div className="form-group">
                    <label>
                      <i className="fas fa-tag"></i>
                      نوع المادة
                    </label>
                    <select
                      value={materialFormData.material_type}
                      onChange={(e) => {
                        handleMaterialFormChange('material_type', e.target.value);
                        // Reset form when changing material type
                        if (!editingMaterial) {
                          setMaterialFormData(prev => ({
                            ...prev,
                            material_type: e.target.value,
                            panel_size_length: '',
                            panel_size_width: '',
                            panel_size_sqm: '',
                            panel_output_watts: '',
                            panel_price: '',
                            mounting_frame_price: '',
                            electrical_price: '',
                            available_quantity: '',
                            battery_capacity_kwh: '',
                            battery_dod_percentage: '',
                            inverter_capacity_kw: '',
                            brand: '',
                            description: '',
                            is_active: true,
                          }));
                        }
                      }}
                      required
                    >
                      <option value="panel">لوح شمسي</option>
                      <option value="battery">بطارية</option>
                      <option value="inverter">محول</option>
                    </select>
                  </div>

                  {materialFormData.material_type === 'panel' && (
                    <>
                      <div className="form-group">
                        <label>
                          <i className="fas fa-square"></i>
                          قياس اللوح بالمتر المربع (م²)
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={materialFormData.panel_size_sqm}
                          onChange={(e) => handleMaterialFormChange('panel_size_sqm', e.target.value)}
                          placeholder="مثال: 2.0"
                          required={materialFormData.material_type === 'panel'}
                        />
                      </div>

                      <div className="form-group">
                        <label>
                          <i className="fas fa-bolt"></i>
                          إنتاجية اللوح بالواط (W)
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={materialFormData.panel_output_watts}
                          onChange={(e) => handleMaterialFormChange('panel_output_watts', e.target.value)}
                          placeholder="مثال: 615"
                          required={materialFormData.material_type === 'panel'}
                        />
                      </div>

                      <div className="form-group">
                        <label>
                          <i className="fas fa-dollar-sign"></i>
                          سعر اللوح بالدولار ($)
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={materialFormData.panel_price}
                          onChange={(e) => handleMaterialFormChange('panel_price', e.target.value)}
                          placeholder="مثال: 150"
                          required={materialFormData.material_type === 'panel'}
                        />
                      </div>

                      <div className="form-group">
                        <label>
                          <i className="fas fa-box"></i>
                          العدد المتوفر
                        </label>
                        <input
                          type="number"
                          step="1"
                          min="0"
                          value={materialFormData.available_quantity}
                          onChange={(e) => handleMaterialFormChange('available_quantity', e.target.value)}
                          placeholder="مثال: 50"
                          required={materialFormData.material_type === 'panel'}
                        />
                      </div>

                      <div className="form-group">
                        <label>
                          <i className="fas fa-tools"></i>
                          سعر الهيكل المعدني لكل لوح ($)
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={materialFormData.mounting_frame_price}
                          onChange={(e) => handleMaterialFormChange('mounting_frame_price', e.target.value)}
                          placeholder="مثال: 20"
                        />
                      </div>

                      <div className="form-group">
                        <label>
                          <i className="fas fa-plug"></i>
                          سعر الكهربائيات (قواطع وأسلاك DC) لكل لوح ($)
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={materialFormData.electrical_price}
                          onChange={(e) => handleMaterialFormChange('electrical_price', e.target.value)}
                          placeholder="مثال: 10"
                        />
                      </div>
                    </>
                  )}

                  {materialFormData.material_type === 'battery' && (
                    <>
                      <div className="form-group">
                        <label>
                          <i className="fas fa-battery-full"></i>
                          حجم البطارية (كيلو واط بالساعة)
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={materialFormData.battery_capacity_kwh}
                          onChange={(e) => handleMaterialFormChange('battery_capacity_kwh', e.target.value)}
                          placeholder="مثال: 10.2"
                          required={materialFormData.material_type === 'battery'}
                        />
                      </div>

                      <div className="form-group">
                        <label>
                          <i className="fas fa-percentage"></i>
                          عمق التفريغ (بالنسبة المئوية)
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          max="100"
                          value={materialFormData.battery_dod_percentage}
                          onChange={(e) => handleMaterialFormChange('battery_dod_percentage', e.target.value)}
                          placeholder="مثال: 0.9 (يعني 90%)"
                          required={materialFormData.material_type === 'battery'}
                        />
                        <small className="form-hint">أدخل قيمة بين 0 و 1 (مثلاً 0.9 يعني 90%)</small>
                      </div>

                      <div className="form-group">
                        <label>
                          <i className="fas fa-dollar-sign"></i>
                          سعر البطارية ($)
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={materialFormData.panel_price}
                          onChange={(e) => handleMaterialFormChange('panel_price', e.target.value)}
                          placeholder="مثال: 500"
                          required={materialFormData.material_type === 'battery'}
                        />
                      </div>

                      <div className="form-group">
                        <label>
                          <i className="fas fa-box"></i>
                          العدد المتوفر
                        </label>
                        <input
                          type="number"
                          step="1"
                          min="0"
                          value={materialFormData.available_quantity}
                          onChange={(e) => handleMaterialFormChange('available_quantity', e.target.value)}
                          placeholder="مثال: 20"
                          required={materialFormData.material_type === 'battery'}
                        />
                      </div>
                    </>
                  )}

                  {materialFormData.material_type === 'inverter' && (
                    <>
                      <div className="form-group">
                        <label>
                          <i className="fas fa-exchange-alt"></i>
                          حجم المحول (كيلو واط)
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={materialFormData.inverter_capacity_kw}
                          onChange={(e) => handleMaterialFormChange('inverter_capacity_kw', e.target.value)}
                          placeholder="مثال: 5.0"
                          required={materialFormData.material_type === 'inverter'}
                        />
                      </div>

                      <div className="form-group">
                        <label>
                          <i className="fas fa-dollar-sign"></i>
                          سعر المحول ($)
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={materialFormData.panel_price}
                          onChange={(e) => handleMaterialFormChange('panel_price', e.target.value)}
                          placeholder="مثال: 800"
                          required={materialFormData.material_type === 'inverter'}
                        />
                      </div>

                      <div className="form-group">
                        <label>
                          <i className="fas fa-box"></i>
                          العدد المتوفر
                        </label>
                        <input
                          type="number"
                          step="1"
                          min="0"
                          value={materialFormData.available_quantity}
                          onChange={(e) => handleMaterialFormChange('available_quantity', e.target.value)}
                          placeholder="مثال: 15"
                          required={materialFormData.material_type === 'inverter'}
                        />
                      </div>
                    </>
                  )}

                  <div className="form-group">
                    <label>
                      <i className="fas fa-trademark"></i>
                      الماركة
                    </label>
                    <input
                      type="text"
                      value={materialFormData.brand}
                      onChange={(e) => handleMaterialFormChange('brand', e.target.value)}
                      placeholder="مثال: SunPower"
                    />
                  </div>

                  <div className="form-group">
                    <label>
                      <i className="fas fa-align-right"></i>
                      الوصف (اختياري)
                    </label>
                    <textarea
                      value={materialFormData.description}
                      onChange={(e) => handleMaterialFormChange('description', e.target.value)}
                      rows="3"
                      placeholder="وصف إضافي للمادة..."
                    />
                  </div>

                  <div className="form-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={materialFormData.is_active}
                        onChange={(e) => handleMaterialFormChange('is_active', e.target.checked)}
                      />
                      {' '}مادة نشطة
                    </label>
                  </div>

                  <div className="form-actions">
                    <button type="submit" className="btn-primary" disabled={loading}>
                      {loading ? (
                        <>
                          <i className="fas fa-spinner fa-spin"></i>
                          جاري الحفظ...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-save"></i>
                          {editingMaterial 
                            ? 'حفظ التعديلات' 
                            : (materialFormData.material_type === 'panel' ? 'إضافة اللوح' : materialFormData.material_type === 'battery' ? 'إضافة البطارية' : materialFormData.material_type === 'inverter' ? 'إضافة المحول' : 'إضافة المادة')
                          }
                        </>
                      )}
                    </button>
                    <button type="button" onClick={() => setShowMaterialForm(false)} className="btn-secondary">
                      إلغاء
                    </button>
                  </div>
                </form>
              </div>
            </div>,
            document.body
          )}

          {showQuoteForm && (
            <div className="modal">
              <div className="modal-content">
                <h2>إنشاء عرض سعر</h2>
                <form onSubmit={handleQuoteSubmit}>
                  <div className="form-group">
                    <label>اسم المادة</label>
                    <input type="text" required />
                  </div>
                  <div className="form-group">
                    <label>الكمية</label>
                    <input type="number" required />
                  </div>
                  <div className="form-group">
                    <label>سعر الوحدة</label>
                    <input type="number" required />
                  </div>
                  <button type="submit" className="btn-primary">إنشاء العرض</button>
                  <button type="button" onClick={() => setShowQuoteForm(false)}>إلغاء</button>
                </form>
              </div>
            </div>
          )}

          {/* Request Details Modal */}
          {showRequestDetails && selectedRequest && (
            <div className="modal" style={{ zIndex: 2000, position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={(e) => {
              if (e.target.classList.contains('modal')) {
                setShowRequestDetails(false);
              }
            }}>
              <div className="modal-content" style={{ maxWidth: '900px', maxHeight: '90vh', overflowY: 'auto', zIndex: 2001, background: 'white', borderRadius: '16px', padding: '30px', position: 'relative' }} onClick={(e) => e.stopPropagation()}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px' }}>
                  <h2 style={{ margin: 0, color: '#2d3748' }}>
                    <i className="fas fa-info-circle"></i> تفاصيل الطلب #{selectedRequest.id || 'N/A'}
                  </h2>
                  <button 
                    onClick={() => setShowRequestDetails(false)}
                    style={{
                      background: 'none',
                      border: 'none',
                      fontSize: '28px',
                      cursor: 'pointer',
                      color: '#718096',
                      padding: '5px 10px',
                      lineHeight: '1',
                      fontWeight: 'bold'
                    }}
                    onMouseOver={(e) => e.target.style.color = '#2d3748'}
                    onMouseOut={(e) => e.target.style.color = '#718096'}
                  >
                    ×
                  </button>
                </div>

                {/* Customer Info */}
                <div style={{ background: '#f7fafc', padding: '20px', borderRadius: '12px', marginBottom: '20px' }}>
                  <h3 style={{ color: '#2d3748', marginBottom: '15px', fontSize: '18px' }}>
                    <i className="fas fa-user"></i> معلومات العميل
                  </h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px' }}>
                    <p><strong>الاسم:</strong> {selectedRequest.full_name}</p>
                    <p><strong>الهاتف:</strong> {selectedRequest.phone_number}</p>
                    <p><strong>المساحة:</strong> {selectedRequest.area_sqm} م²</p>
                  </div>
                </div>

                {/* Consumption Info */}
                {selectedRequest.daytime_consumption_amps && (
                  <div style={{ background: '#f7fafc', padding: '20px', borderRadius: '12px', marginBottom: '20px' }}>
                    <h3 style={{ color: '#667eea', marginBottom: '15px', fontSize: '18px' }}>
                      <i className="fas fa-bolt"></i> معلومات الاستهلاك
                    </h3>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px' }}>
                      <p><strong>الاستهلاك النهاري:</strong> {selectedRequest.daytime_consumption_amps} أمبير</p>
                      {selectedRequest.nightly_consumption_amps_per_hour && (
                        <p><strong>الاستهلاك الليلي:</strong> {selectedRequest.nightly_consumption_amps_per_hour} أمبير/ساعة</p>
                      )}
                      {selectedRequest.battery_hours_night && (
                        <p><strong>عدد ساعات الليل:</strong> {selectedRequest.battery_hours_night} ساعة</p>
                      )}
                    </div>
                  </div>
                )}

                {/* System Requirements */}
                {selectedRequest.required_system_size_kw && (
                  <div style={{ background: '#f7fafc', padding: '20px', borderRadius: '12px', marginBottom: '20px' }}>
                    <h3 style={{ color: '#667eea', marginBottom: '15px', fontSize: '18px' }}>
                      <i className="fas fa-calculator"></i> متطلبات المنظومة
                    </h3>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px' }}>
                      <p style={{ fontSize: '16px', color: '#667eea', fontWeight: 'bold' }}>
                        <strong>حجم المنظومة المطلوب:</strong> {parseFloat(selectedRequest.required_system_size_kw).toFixed(2)} كيلو واط
                      </p>
                      {selectedRequest.required_battery_capacity && (
                        <p style={{ fontSize: '16px' }}>
                          <strong>سعة البطارية المطلوبة:</strong> {parseFloat(selectedRequest.required_battery_capacity).toFixed(2)} واط/ساعة
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Supplier Panels Calculation */}
                {selectedRequest.supplier_panels_calculation && selectedRequest.supplier_panels_calculation.length > 0 && (
                  <div style={{ background: '#f0fff4', padding: '20px', borderRadius: '12px', marginBottom: '20px', border: '1px solid #9ae6b4' }}>
                    <h3 style={{ color: '#48bb78', marginBottom: '20px', fontSize: '18px' }}>
                      <i className="fas fa-solar-panel"></i> حساب الألواح حسب موادي
                    </h3>
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', 
                      gap: '15px'
                    }}>
                      {selectedRequest.supplier_panels_calculation.map((panelCalc, index) => (
                        <div key={panelCalc.panel_id || index} style={{ 
                          padding: '18px', 
                          background: 'white', 
                          borderRadius: '10px',
                          border: '2px solid #9ae6b4',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                        }}>
                          <p style={{ fontWeight: 'bold', color: '#2d3748', marginBottom: '12px', fontSize: '16px' }}>
                            {panelCalc.brand || `لوح ${index + 1}`}
                          </p>
                          <div style={{ fontSize: '14px', color: '#4a5568', lineHeight: '1.8' }}>
                            <p><strong>الإنتاجية:</strong> {panelCalc.panel_output_watts} واط</p>
                            <p><strong>القياس:</strong> {panelCalc.panel_size_sqm} م²</p>
                            {panelCalc.calculated_panels_count && (
                              <p style={{ color: '#667eea', fontWeight: 'bold', marginTop: '10px' }}>
                                <strong>عدد الألواح المطلوبة:</strong> {panelCalc.calculated_panels_count} لوح
                              </p>
                            )}
                            {panelCalc.available_quantity !== undefined && (
                              <p style={{ color: panelCalc.calculated_panels_count > panelCalc.available_quantity ? '#e53e3e' : '#48bb78', fontWeight: '600' }}>
                                <strong>المتوفر:</strong> {panelCalc.available_quantity} لوح
                              </p>
                            )}
                            {panelCalc.total_cost && (
                              <p style={{ marginTop: '12px', paddingTop: '12px', borderTop: '2px solid #e2e8f0', fontWeight: 'bold', color: '#2d3748', fontSize: '15px' }}>
                                <strong>التكلفة الإجمالية:</strong> ${parseFloat(panelCalc.total_cost).toFixed(2)}
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Supplier Batteries Calculation */}
                {selectedRequest.supplier_batteries_calculation && selectedRequest.supplier_batteries_calculation.length > 0 && (
                  <div style={{ background: '#fffaf0', padding: '20px', borderRadius: '12px', marginBottom: '20px', border: '1px solid #fbd38d' }}>
                    <h3 style={{ color: '#ed8936', marginBottom: '20px', fontSize: '18px' }}>
                      <i className="fas fa-battery-full"></i> حساب البطاريات حسب موادي
                    </h3>
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', 
                      gap: '15px'
                    }}>
                      {selectedRequest.supplier_batteries_calculation.map((batteryCalc, index) => (
                        <div key={batteryCalc.battery_id || index} style={{ 
                          padding: '18px', 
                          background: 'white', 
                          borderRadius: '10px',
                          border: '2px solid #fbd38d',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                        }}>
                          <p style={{ fontWeight: 'bold', color: '#2d3748', marginBottom: '12px', fontSize: '16px' }}>
                            {batteryCalc.brand || `بطارية ${index + 1}`}
                          </p>
                          <div style={{ fontSize: '14px', color: '#4a5568', lineHeight: '1.8' }}>
                            <p><strong>السعة:</strong> {batteryCalc.battery_capacity_kwh} كيلو واط/ساعة</p>
                            <p><strong>عمق التفريغ:</strong> {(parseFloat(batteryCalc.battery_dod_percentage) * 100).toFixed(1)}%</p>
                            {batteryCalc.calculated_battery_capacity && (
                              <p style={{ color: '#ed8936', fontWeight: 'bold', marginTop: '10px' }}>
                                <strong>السعة المطلوبة:</strong> {parseFloat(batteryCalc.calculated_battery_capacity).toFixed(2)} واط/ساعة
                              </p>
                            )}
                            {batteryCalc.calculated_batteries_count && (
                              <p style={{ color: '#667eea', fontWeight: 'bold' }}>
                                <strong>عدد البطاريات المطلوبة:</strong> {batteryCalc.calculated_batteries_count} بطارية
                              </p>
                            )}
                            {batteryCalc.available_quantity !== undefined && (
                              <p style={{ color: (batteryCalc.calculated_batteries_count > batteryCalc.available_quantity) ? '#e53e3e' : '#48bb78', fontWeight: '600' }}>
                                <strong>المتوفر:</strong> {batteryCalc.available_quantity} بطارية
                              </p>
                            )}
                            {batteryCalc.total_cost && (
                              <p style={{ marginTop: '12px', paddingTop: '12px', borderTop: '2px solid #e2e8f0', fontWeight: 'bold', color: '#2d3748', fontSize: '15px' }}>
                                <strong>التكلفة الإجمالية:</strong> ${parseFloat(batteryCalc.total_cost).toFixed(2)}
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '25px', paddingTop: '20px', borderTop: '1px solid #e2e8f0' }}>
                  <button onClick={() => setShowRequestDetails(false)} className="btn-secondary" style={{ padding: '10px 20px' }}>
                    إغلاق
                  </button>
                  <button onClick={() => { setShowRequestDetails(false); calculateAvailableQuotes(selectedRequest); }} className="btn-primary" style={{ padding: '10px 20px' }}>
                    <i className="fas fa-list"></i> العروض المتوفرة
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Available Quotes Modal */}
          {showAvailableQuotes && selectedRequest && (
            <div className="modal" style={{ zIndex: 2000, position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={(e) => {
              if (e.target.classList.contains('modal')) {
                setShowAvailableQuotes(false);
              }
            }}>
              <div className="modal-content" style={{ maxWidth: '1000px', maxHeight: '90vh', overflowY: 'auto', zIndex: 2001, background: 'white', borderRadius: '16px', padding: '30px', position: 'relative' }} onClick={(e) => e.stopPropagation()}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px' }}>
                  <h2 style={{ margin: 0, color: '#2d3748' }}>
                    <i className="fas fa-list"></i> العروض المتوفرة ({availableQuotes.length} عرض)
                  </h2>
                  <button 
                    onClick={() => setShowAvailableQuotes(false)}
                    style={{
                      background: 'none',
                      border: 'none',
                      fontSize: '28px',
                      cursor: 'pointer',
                      color: '#718096',
                      padding: '5px 10px',
                      lineHeight: '1',
                      fontWeight: 'bold'
                    }}
                    onMouseOver={(e) => e.target.style.color = '#2d3748'}
                    onMouseOut={(e) => e.target.style.color = '#718096'}
                  >
                    ×
                  </button>
                </div>

                {availableQuotes.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#718096' }}>
                    <i className="fas fa-inbox" style={{ fontSize: '48px', marginBottom: '15px' }}></i>
                    <p>لا توجد عروض متوفرة حالياً</p>
                  </div>
                ) : (
                  <div style={{ display: 'grid', gap: '20px' }}>
                    {availableQuotes.map((quote, index) => (
                      <div key={index} style={{
                        background: index === 0 ? '#f0fff4' : '#f7fafc',
                        border: `2px solid ${index === 0 ? '#48bb78' : '#e2e8f0'}`,
                        borderRadius: '12px',
                        padding: '20px',
                        position: 'relative'
                      }}>
                        {index === 0 && (
                          <div style={{
                            position: 'absolute',
                            top: '10px',
                            left: '10px',
                            background: '#48bb78',
                            color: 'white',
                            padding: '5px 15px',
                            borderRadius: '20px',
                            fontSize: '12px',
                            fontWeight: 'bold'
                          }}>
                            الأفضل سعراً
                          </div>
                        )}
                        
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginTop: index === 0 ? '30px' : '0' }}>
                          {/* Panel Info */}
                          <div>
                            <h4 style={{ color: '#48bb78', marginBottom: '10px', fontSize: '16px' }}>
                              <i className="fas fa-solar-panel"></i> اللوح
                            </h4>
                            <p style={{ fontSize: '14px', margin: '5px 0' }}>
                              <strong>{quote.panel.brand || 'لوح'}</strong>
                            </p>
                            <p style={{ fontSize: '13px', color: '#4a5568', margin: '3px 0' }}>
                              {quote.panel.calculated_panels_count} لوح × ${(parseFloat(quote.panel.panel_price || 0) + parseFloat(quote.panel.mounting_frame_price || 0) + parseFloat(quote.panel.electrical_price || 0)).toFixed(2)}
                            </p>
                            <p style={{ fontSize: '14px', fontWeight: 'bold', color: '#2d3748', marginTop: '8px' }}>
                              ${quote.panelsTotalPrice.toFixed(2)}
                            </p>
                          </div>

                          {/* Battery Info */}
                          <div>
                            <h4 style={{ color: '#ed8936', marginBottom: '10px', fontSize: '16px' }}>
                              <i className="fas fa-battery-full"></i> البطارية
                            </h4>
                            <p style={{ fontSize: '14px', margin: '5px 0' }}>
                              <strong>{quote.battery.brand || 'بطارية'}</strong>
                            </p>
                            <p style={{ fontSize: '13px', color: '#4a5568', margin: '3px 0' }}>
                              {quote.battery.calculated_batteries_count} بطارية × ${parseFloat(quote.battery.battery_price || 0).toFixed(2)}
                            </p>
                            <p style={{ fontSize: '14px', fontWeight: 'bold', color: '#2d3748', marginTop: '8px' }}>
                              ${quote.batteriesTotalPrice.toFixed(2)}
                            </p>
                          </div>

                          {/* Inverter Info */}
                          <div>
                            <h4 style={{ color: '#667eea', marginBottom: '10px', fontSize: '16px' }}>
                              <i className="fas fa-exchange-alt"></i> المحول
                            </h4>
                            <p style={{ fontSize: '14px', margin: '5px 0' }}>
                              <strong>{quote.inverter.brand || 'محول'}</strong>
                            </p>
                            <p style={{ fontSize: '13px', color: '#4a5568', margin: '3px 0' }}>
                              {quote.inverter.inverter_capacity_kw} كيلو واط
                            </p>
                            <p style={{ fontSize: '14px', fontWeight: 'bold', color: '#2d3748', marginTop: '8px' }}>
                              ${quote.inverterPrice.toFixed(2)}
                            </p>
                          </div>
                        </div>

                        {/* Additional Services */}
                        <div style={{ marginTop: '15px', paddingTop: '15px', borderTop: '1px solid #e2e8f0' }}>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '10px', fontSize: '13px', color: '#4a5568' }}>
                            {quote.groundingPrice > 0 && (
                              <p><strong>التأريض:</strong> ${quote.groundingPrice.toFixed(2)}</p>
                            )}
                            {quote.fireFightingPrice > 0 && (
                              <p><strong>مكافحة الحريق:</strong> ${quote.fireFightingPrice.toFixed(2)}</p>
                            )}
                            {quote.installationPrice > 0 && (
                              <p><strong>التركيب والصيانة:</strong> ${quote.installationPrice.toFixed(2)}</p>
                            )}
                          </div>
                        </div>

                        {/* Total Price */}
                        <div style={{ marginTop: '15px', paddingTop: '15px', borderTop: '2px solid #667eea', textAlign: 'center' }}>
                          <p style={{ fontSize: '24px', fontWeight: 'bold', color: '#667eea', margin: 0 }}>
                            السعر الإجمالي: ${quote.totalPrice.toFixed(2)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '25px', paddingTop: '20px', borderTop: '1px solid #e2e8f0' }}>
                  <button onClick={() => setShowAvailableQuotes(false)} className="btn-secondary" style={{ padding: '10px 20px' }}>
                    إغلاق
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default SupplierDashboard;
